import { ShowInternalProjectLayout } from '@/components';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { useGetInternalProjectDetailsByProjectIdQuery, useGetExternalProjectDetailsQuery } from '@/state';
import type { ApiErrorResponse } from '@/types';
import { useParams, useSearchParams } from 'react-router-dom';

const ShowProjectDetails = () => {
  const { tenant } = useTenant();
  const { isTeacher, isSchoolAdmin, isCompany, isLoading: permissionsLoading } = useClientPermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const projectResourcesId =
    searchParams.get('projectResourcesId') ?? undefined;
  const searchProjectId = searchParams.get('projectId') ?? undefined;
  const projectType = searchParams.get('type') ?? 'INTERNAL';

  const isAssigned = !!projectResourcesId;
  const effectiveId = isAssigned ? Number(projectResourcesId) : Number(projectId);
  const isExternal = projectType === 'EXTERNAL';

  const readyToFetch = !permissionsLoading && !!tenant && !!effectiveId;

  const {
    data: internalProjectDetails,
    isLoading: internalLoading,
    error: internalError,
    isFetching: internalFetching,
  } = useGetInternalProjectDetailsByProjectIdQuery({
    tenant: tenant!,
    projectId: effectiveId,
    isTeacher: isTeacher || isSchoolAdmin,
    isAssigned,
  }, { skip: !readyToFetch || isExternal });

  const {
    data: externalProjectDetails,
    isLoading: externalLoading,
    error: externalError,
    isFetching: externalFetching,
  } = useGetExternalProjectDetailsQuery({
    tenant: tenant!,
    id: effectiveId,
    isCompany,
    isAssigned,
  }, { skip: !readyToFetch || !isExternal });

  const projectDetails = isExternal ? externalProjectDetails : internalProjectDetails;

  const isLoading = isExternal ? externalLoading : internalLoading;
  const error = isExternal ? externalError : internalError;
  const isFetching = isExternal ? externalFetching : internalFetching;

  return (
    <>
      <WithLoadingErrorWrapper
        loading={isLoading || permissionsLoading}
        error={error as ApiErrorResponse}
        isFetching={isFetching}
      >
        {projectDetails && (
          <ShowInternalProjectLayout
            projectDetails={projectDetails}
            detailedId={effectiveId}
            passedProjectId={searchProjectId ? Number(searchProjectId) : undefined}
            routeProjectId={Number(projectId)}
            isAssignedView={isAssigned}
            isCompany={isCompany}
          />
        )}
      </WithLoadingErrorWrapper>
    </>
  );
};

export default ShowProjectDetails;
